/**
 * Usage analytics, read from `dailyAggregates` — one document per day, written
 * by the aggregateDaily function at midnight IST.
 *
 * One document read per day shown, so a 30-day view costs 30 reads. Nothing
 * here touches the `events` collection, which has a row per ovi read and would
 * be thousands of reads to scan.
 *
 * WHAT IS REAL, and what is not: the app only ever emits two event types,
 * `ovi_view` (from the reader) and `ovi_listen` (from the players). So
 * totalUsers, ovisRead, ovisListened, the two duration totals and topOvis
 * carry real data; totalSessions, sharesCount, searchesCount, downloadsCount,
 * topChapters, platformBreakdown and retentionRate are fields the aggregate
 * declares but nothing ever fills, and are always zero. `isInstrumented`
 * below is the single place that knowledge lives — the page shows a metric
 * only if it appears there.
 */
import { collection, getDocs, limit, orderBy, query } from 'firebase/firestore';
import { COLLECTIONS } from '@dnyaneshwari/shared';
import type { DailyAggregate } from '@dnyaneshwari/shared';
import { db } from './firebase';

/** Metrics the app actually records today. Anything else is always zero. */
export const INSTRUMENTED = {
  totalUsers: true,
  newUsers: true,
  ovisRead: true,
  ovisListened: true,
  totalReadingTimeMs: true,
  totalListeningTimeMs: true,
  topOvis: true,
  // Declared by the aggregate, never written by the app — see the note above.
  totalSessions: false,
  sharesCount: false,
  searchesCount: false,
  downloadsCount: false,
  topChapters: false,
  platformBreakdown: false,
  retentionRate: false,
  totalSlideViewTimeMs: false,
} as const;

function toAggregate(id: string, d: Record<string, unknown>): DailyAggregate {
  const n = (k: string) => (typeof d[k] === 'number' ? (d[k] as number) : 0);
  return {
    id,
    date: typeof d.date === 'string' ? d.date : id,
    totalUsers: n('totalUsers'),
    newUsers: n('newUsers'),
    totalSessions: n('totalSessions'),
    totalReadingTimeMs: n('totalReadingTimeMs'),
    totalListeningTimeMs: n('totalListeningTimeMs'),
    totalSlideViewTimeMs: n('totalSlideViewTimeMs'),
    ovisRead: n('ovisRead'),
    ovisListened: n('ovisListened'),
    sharesCount: n('sharesCount'),
    searchesCount: n('searchesCount'),
    downloadsCount: n('downloadsCount'),
    notificationsSent: n('notificationsSent'),
    notificationsOpened: n('notificationsOpened'),
    topChapters: Array.isArray(d.topChapters) ? (d.topChapters as DailyAggregate['topChapters']) : [],
    topOvis: Array.isArray(d.topOvis) ? (d.topOvis as DailyAggregate['topOvis']) : [],
    platformBreakdown: (d.platformBreakdown as DailyAggregate['platformBreakdown']) ?? { android: 0, ios: 0, web: 0 },
    retentionRate: n('retentionRate'),
  };
}

/**
 * The last `days` daily aggregates, oldest first so a chart can plot them
 * directly. Days the function has not written simply do not appear.
 */
export async function getDailyAggregates(days: number): Promise<DailyAggregate[]> {
  const snap = await getDocs(
    query(collection(db, COLLECTIONS.DAILY_AGGREGATES), orderBy('date', 'desc'), limit(days))
  );
  return snap.docs.map((d) => toAggregate(d.id, d.data())).reverse();
}

export interface Totals {
  /** Sum of each day's distinct readers/listeners — a visit count, not people. */
  activeDaySum: number;
  newUsers: number;
  ovisRead: number;
  ovisListened: number;
  readingMs: number;
  listeningMs: number;
  /** Highest single day of readers/listeners in the period. */
  peakActive: number;
  /** The most recent day that has an aggregate at all. */
  latest: DailyAggregate | null;
  /** Mean readers/listeners per day across the days present. */
  avgActive: number;
}

export function summarise(rows: DailyAggregate[]): Totals {
  const sum = (f: (r: DailyAggregate) => number) => rows.reduce((a, r) => a + f(r), 0);
  return {
    activeDaySum: sum((r) => r.totalUsers),
    newUsers: sum((r) => r.newUsers),
    ovisRead: sum((r) => r.ovisRead),
    ovisListened: sum((r) => r.ovisListened),
    readingMs: sum((r) => r.totalReadingTimeMs),
    listeningMs: sum((r) => r.totalListeningTimeMs),
    peakActive: rows.reduce((a, r) => Math.max(a, r.totalUsers), 0),
    latest: rows.length ? rows[rows.length - 1] : null,
    avgActive: rows.length ? Math.round(sum((r) => r.totalUsers) / rows.length) : 0,
  };
}

/** The period's most-read ovis, merged across every day's top-ten list. */
export function mergeTopOvis(rows: DailyAggregate[], take = 10): { oviId: string; views: number }[] {
  const totals = new Map<string, number>();
  for (const row of rows) {
    for (const { oviId, views } of row.topOvis) {
      totals.set(oviId, (totals.get(oviId) ?? 0) + views);
    }
  }
  return [...totals.entries()]
    .map(([oviId, views]) => ({ oviId, views }))
    .sort((a, b) => b.views - a.views)
    .slice(0, take);
}

/** The loaded rows as CSV — no further reads. */
export function toCsv(rows: DailyAggregate[]): string {
  const head = ['date', 'read_or_listened', 'new_users', 'ovis_read', 'ovis_listened', 'reading_minutes', 'listening_minutes'];
  const body = rows.map((r) => [
    r.date,
    r.totalUsers,
    r.newUsers,
    r.ovisRead,
    r.ovisListened,
    Math.round(r.totalReadingTimeMs / 60000),
    Math.round(r.totalListeningTimeMs / 60000),
  ].join(','));
  return [head.join(','), ...body].join('\n');
}
